import React, { useState, useEffect, useRef } from 'react';
import api from '../api';
import {
  FileText, SpinnerGap, Robot, CheckCircle, Clock, Warning, ArrowRight,
  PencilSimple, FloppyDisk, CaretDown, CaretUp, Printer, ArrowClockwise, X
} from '@phosphor-icons/react';

const STATUS_LABELS = {
  draft: { label: 'Draft', color: 'bg-gray-100 text-gray-700' },
  data_owner_review: { label: 'Data Owner Review', color: 'bg-blue-50 text-blue-700' },
  sustainability_review: { label: 'Sustainability Review', color: 'bg-amber-50 text-amber-700' },
  executive_approval: { label: 'Executive Approval', color: 'bg-purple-50 text-purple-700' },
  final: { label: 'Final', color: 'bg-status-on-track-bg text-status-on-track' },
};

const STEP_LABELS = ['Draft', 'Data Owner', 'Sustainability', 'Executive', 'Final'];

function WorkflowStepper({ status }) {
  const steps = ['draft', 'data_owner_review', 'sustainability_review', 'executive_approval', 'final'];
  const idx = steps.indexOf(status);
  return (
    <div data-testid="report-stepper" className="flex items-center gap-1 overflow-x-auto py-2">
      {steps.map((s, i) => (
        <React.Fragment key={s}>
          <div className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-[9px] font-semibold whitespace-nowrap ${
            i < idx ? 'bg-status-on-track-bg text-status-on-track' : i === idx ? 'bg-brand-primary text-white' : 'bg-surface-muted text-content-disabled'}`}>
            {i < idx ? <CheckCircle size={11} /> : i === idx ? <Clock size={11} /> : <span className="w-3 h-3 rounded-full border border-current flex items-center justify-center text-[7px]">{i + 1}</span>}
            {STEP_LABELS[i]}
          </div>
          {i < steps.length - 1 && <ArrowRight size={10} className={i < idx ? 'text-status-on-track' : 'text-content-disabled'} />}
        </React.Fragment>
      ))}
    </div>
  );
}

function SectionEditor({ section, reportId, status, onUpdate }) {
  const [editing, setEditing] = useState(false);
  const [content, setContent] = useState(section.content);
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.put('/api/env/report/section', { report_id: reportId, section_key: section.key, content });
      onUpdate(section.key, content);
      setEditing(false);
    } catch {}
    setSaving(false);
  };

  const isFinal = status === 'final';
  const preview = content?.slice(0, 200);
  const isLong = content?.length > 200;

  return (
    <div data-testid={`section-${section.key}`} className="border border-border-default rounded-lg overflow-hidden">
      <div className="flex items-center justify-between px-4 py-2.5 bg-surface-muted cursor-pointer" onClick={() => setExpanded(!expanded)}>
        <div className="flex items-center gap-2">
          <FileText size={13} className="text-brand-primary" />
          <h4 className="text-xs font-semibold text-content-primary">{section.title}</h4>
          {section.edited && <span className="text-[8px] px-1.5 py-0.5 bg-blue-50 text-blue-600 rounded font-semibold">Edited by {section.edited_by}</span>}
        </div>
        <div className="flex items-center gap-2">
          {!isFinal && !editing && (
            <button onClick={e => { e.stopPropagation(); setEditing(true); setExpanded(true); }} className="text-[9px] px-2 py-1 bg-white border border-border-default rounded text-content-secondary hover:bg-surface-muted">
              <PencilSimple size={10} className="inline mr-1" />Edit
            </button>
          )}
          {expanded ? <CaretUp size={12} /> : <CaretDown size={12} />}
        </div>
      </div>
      {expanded && (
        <div className="px-4 py-3">
          {editing ? (
            <div className="space-y-2">
              <textarea value={content} onChange={e => setContent(e.target.value)} rows={10} className="w-full px-3 py-2 border border-border-default rounded-lg text-xs text-content-primary leading-relaxed resize-y focus:ring-1 focus:ring-brand-primary focus:border-brand-primary" />
              <div className="flex gap-2 justify-end">
                <button onClick={() => { setEditing(false); setContent(section.content); }} className="px-3 py-1.5 text-xs border border-border-default rounded-lg hover:bg-surface-muted">Cancel</button>
                <button data-testid={`save-${section.key}`} onClick={save} disabled={saving} className="px-3 py-1.5 text-xs bg-brand-primary text-white rounded-lg hover:bg-brand-primary-hover disabled:opacity-50 flex items-center gap-1">
                  {saving ? <SpinnerGap size={11} className="animate-spin" /> : <FloppyDisk size={11} />} Save
                </button>
              </div>
            </div>
          ) : (
            <div className="text-xs text-content-secondary leading-relaxed whitespace-pre-line">
              {expanded || !isLong ? content : preview + '...'}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function AnnualReportStudio() {
  const [report, setReport] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [advancing, setAdvancing] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const printRef = useRef(null);

  useEffect(() => {
    Promise.all([
      api.get('/api/env/report/latest').then(r => setReport(r.data.report)),
      api.get('/api/env/report/history').then(r => setHistory(r.data)),
    ]).finally(() => setLoading(false));
  }, []);

  const generate = async () => {
    setGenerating(true);
    try {
      const resp = await api.post('/api/env/report/generate', { year: new Date().getFullYear() });
      setReport(resp.data);
      setHistory(prev => [resp.data, ...prev]);
    } catch {}
    setGenerating(false);
  };

  const advance = async () => {
    if (!report) return;
    setAdvancing(true);
    try {
      const resp = await api.post('/api/env/report/advance', { report_id: report.report_id });
      setReport(prev => ({ ...prev, status: resp.data.new_status }));
    } catch {}
    setAdvancing(false);
  };

  const reject = async () => {
    if (!report) return;
    try {
      await api.post('/api/env/report/reject', { report_id: report.report_id, note: 'Needs revision' });
      setReport(prev => ({ ...prev, status: 'draft' }));
    } catch {}
  };

  const onSectionUpdate = (key, content) => {
    setReport(prev => ({ ...prev, sections: prev.sections.map(s => s.key === key ? { ...s, content, edited: true } : s) }));
  };

  const handlePrint = () => {
    const win = window.open('', '_blank');
    const sections = report.sections.map(s => `<h2 style="color:#0A4D3C;margin-top:32px;font-size:18px;border-bottom:2px solid #0A4D3C;padding-bottom:8px">${s.title}</h2><div style="white-space:pre-line;line-height:1.7;font-size:13px;color:#333">${s.content}</div>`).join('');
    win.document.write(`<!DOCTYPE html><html><head><title>${report.title}</title><style>body{font-family:Georgia,serif;max-width:800px;margin:40px auto;padding:0 40px;color:#1A1F1B}h1{color:#0A4D3C;font-size:24px;text-align:center;border-bottom:3px solid #0A4D3C;padding-bottom:12px}@media print{body{margin:20px}}</style></head><body><h1>${report.title}</h1><p style="text-align:center;color:#666;font-size:12px">Bangko Sentral ng Pilipinas | Report ID: ${report.report_id} | Status: ${report.status}</p>${sections}</body></html>`);
    win.document.close();
    setTimeout(() => win.print(), 500);
  };

  if (loading) return <div className="flex items-center justify-center py-16"><SpinnerGap size={24} className="animate-spin text-brand-primary" /><span className="ml-2 text-sm text-content-secondary">Loading Report Studio...</span></div>;

  return (
    <div data-testid="annual-report-studio" className="space-y-4">
      {/* Header Actions */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h3 className="font-heading font-semibold text-base text-content-primary">{report ? report.title : 'No Report Generated'}</h3>
          {report && <p className="text-[10px] text-content-disabled font-mono">{report.report_id} | Created by {report.created_by}</p>}
        </div>
        <div className="flex items-center gap-2">
          {report && (
            <>
              <span className={`px-2.5 py-1 rounded-full text-[9px] font-bold ${STATUS_LABELS[report.status]?.color || 'bg-gray-100 text-gray-700'}`}>
                {STATUS_LABELS[report.status]?.label || report.status}
              </span>
              <button onClick={handlePrint} className="px-3 py-1.5 text-xs border border-border-default rounded-lg hover:bg-surface-muted flex items-center gap-1"><Printer size={12} />Print / PDF</button>
              {report.status !== 'final' && report.status !== 'draft' && (
                <button onClick={reject} className="px-3 py-1.5 text-xs border border-status-alert/30 text-status-alert rounded-lg hover:bg-status-alert-bg flex items-center gap-1"><ArrowClockwise size={12} />Reject</button>
              )}
              {report.status !== 'final' && (
                <button data-testid="advance-report" onClick={advance} disabled={advancing} className="px-3 py-1.5 text-xs bg-brand-primary text-white rounded-lg hover:bg-brand-primary-hover disabled:opacity-50 flex items-center gap-1">
                  {advancing ? <SpinnerGap size={12} className="animate-spin" /> : <ArrowRight size={12} />}
                  Advance Workflow
                </button>
              )}
            </>
          )}
          <button data-testid="generate-report" onClick={generate} disabled={generating} className="px-3 py-1.5 text-xs bg-brand-accent text-white rounded-lg hover:bg-brand-accent/90 disabled:opacity-50 flex items-center gap-1">
            {generating ? <><SpinnerGap size={12} className="animate-spin" />Generating...</> : <><Robot size={12} />Generate New Report</>}
          </button>
        </div>
      </div>

      {/* Workflow Stepper */}
      {report && <WorkflowStepper status={report.status} />}

      {/* Report Sections */}
      {report ? (
        <div className="space-y-2">
          {report.sections.map(section => (
            <SectionEditor key={section.key} section={section} reportId={report.report_id} status={report.status} onUpdate={onSectionUpdate} />
          ))}
        </div>
      ) : (
        <div className="text-center py-16 bg-white rounded-lg border border-border-default">
          <Robot size={40} className="mx-auto text-content-disabled mb-3" />
          <p className="text-sm text-content-secondary">No report generated yet</p>
          <p className="text-xs text-content-disabled mt-1">Click "Generate New Report" to create an AI-drafted annual report</p>
        </div>
      )}

      {/* Workflow Log */}
      {report?.workflow_log?.length > 0 && (
        <div className="bg-white border border-border-default rounded-lg p-4">
          <h4 className="text-xs font-semibold text-content-secondary uppercase mb-2">Workflow History</h4>
          <div className="space-y-1.5">
            {report.workflow_log.slice().reverse().map((log, i) => (
              <div key={i} className="flex items-center gap-3 text-[10px] text-content-secondary">
                <CheckCircle size={10} className="text-status-on-track flex-shrink-0" />
                <span className="font-semibold">{log.action.replace(/_/g, ' ')}</span>
                <span>by {log.by_name} ({log.by_role})</span>
                {log.at && <span className="text-content-disabled">{new Date(log.at).toLocaleString()}</span>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Report History */}
      {history.length > 0 && (
        <div className="bg-white border border-border-default rounded-lg p-4">
          <button onClick={() => setShowHistory(!showHistory)} className="flex items-center gap-2 w-full text-left">
            <h4 className="text-xs font-semibold text-content-secondary uppercase">Report Archive ({history.length})</h4>
            {showHistory ? <CaretUp size={12} /> : <CaretDown size={12} />}
          </button>
          {showHistory && (
            <div className="mt-2 space-y-1.5">
              {history.map((r, i) => (
                <div key={i} className="flex items-center justify-between p-2 bg-surface-default rounded-lg text-[10px]">
                  <div>
                    <span className="font-mono text-content-disabled">{r.report_id}</span>
                    <span className="ml-2 font-semibold text-content-primary">{r.title}</span>
                  </div>
                  <span className={`px-2 py-0.5 rounded-full font-bold ${STATUS_LABELS[r.status]?.color || ''}`}>{STATUS_LABELS[r.status]?.label || r.status}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
